/**
 * Layout for award and completion certificates.
 *
 * Elements sit at fixed fractions of the height inside the border, so the same layout works
 * on portrait and landscape and on Letter and A4. Text is fitted to its band with
 * `fitLines`, so a long title or a long name shrinks rather than overflowing.
 */

import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import { r2 } from "@/lib/tool-kit/printables/page";
import {
  type FontSpec,
  fitLines,
  type TextMeasure,
} from "@/lib/tool-kit/printables/text";

export type BorderStyle = "none" | "simple" | "double" | "classic" | "stars";
export type CertificateFont = "classic" | "modern" | "script";

export const MAX_RECIPIENTS = 100;

/** Accent colours: all dark enough to print legibly in colour or grey. */
export const ACCENTS = {
  navy: "#1f3a68",
  burgundy: "#7b1e2b",
  forest: "#1f5a3a",
  gold: "#8a6a00",
  purple: "#5b2a86",
  black: "#1a1a1a",
} as const;
export type AccentKey = keyof typeof ACCENTS;

export interface FontSet {
  title: FontSpec;
  intro: FontSpec;
  name: FontSpec;
  body: FontSpec;
  label: FontSpec;
}

export function fontSet(style: CertificateFont): FontSet {
  if (style === "modern") {
    return {
      title: { kind: "sans", bold: true },
      intro: { kind: "sans" },
      name: { kind: "sans", bold: true },
      body: { kind: "sans" },
      label: { kind: "sans" },
    };
  }
  return {
    title: { kind: "serif", bold: true },
    intro: { kind: "serif", italic: true },
    name:
      style === "script" ? { kind: "script" } : { kind: "serif", italic: true },
    body: { kind: "serif" },
    label: { kind: "serif" },
  };
}

/** Recipient names: one per line, trimmed, capped. */
export function parseRecipients(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, MAX_RECIPIENTS);
}

export interface CertificateText {
  organisation: string;
  title: string;
  intro: string;
  body: string;
  dateLabel: string;
  dateText: string;
  signatureLabel: string;
}

export interface PlacedLine {
  text: string;
  x: number;
  y: number;
  size: number;
}

export interface Rule {
  x1: number;
  x2: number;
  y: number;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CertificateLayout {
  /** The border rectangle (the page margin inset). */
  border: Box;
  /** The area text lives in, inside the border. */
  inner: Box;
  centerX: number;
  organisation: PlacedLine | null;
  title: PlacedLine[];
  intro: PlacedLine | null;
  nameBaseline: number;
  nameWidth: number;
  nameSizeMax: number;
  body: PlacedLine[];
  /** The write-in line under the recipient's name. */
  nameRule: Rule;
  dateRule: Rule;
  signatureRule: Rule;
  dateLabel: PlacedLine | null;
  dateText: PlacedLine | null;
  signatureLabel: PlacedLine | null;
  seal: { cx: number; cy: number; radius: number } | null;
}

/** A title on one line if it fits at 60% of the full size or better, else on two. */
function fitTitle(
  measure: TextMeasure,
  title: string,
  font: FontSpec,
  width: number,
  maxSize: number
) {
  const single = fitLines(
    measure,
    title,
    font,
    width,
    maxSize,
    maxSize * 0.6,
    1
  );
  if (single.lines.join(" ") === title) {
    return single;
  }
  return fitLines(measure, title, font, width, maxSize, 6, 2);
}

/** Body text: up to four lines, shrunk until the block fits the band between name and seal. */
function fitBody(
  measure: TextMeasure,
  body: string,
  font: FontSpec,
  width: number,
  maxSize: number,
  bandHeight: number
) {
  let block = fitLines(measure, body, font, width, maxSize, 3.2, 4);
  for (
    let cap = maxSize;
    cap > 3.2 && block.size * 1.45 * block.lines.length > bandHeight;
    cap -= 0.25
  ) {
    block = fitLines(measure, body, font, width, cap, 3.2, 4);
  }
  return block;
}

function placed(text: string, x: number, y: number, size: number): PlacedLine {
  return { text, x: r2(x), y: r2(y), size };
}

/** Border inset from the sheet edge, in millimetres: the page margin. */
export function certificateBorder(geometry: PageGeometry): Box {
  return {
    x: geometry.left,
    y: geometry.top,
    width: geometry.contentWidth,
    height: geometry.contentHeight,
  };
}

/** Where everything goes on one certificate. */
export function layoutCertificate(
  geometry: PageGeometry,
  measure: TextMeasure,
  text: CertificateText,
  fonts: FontSet,
  showSeal: boolean
): CertificateLayout {
  const border = certificateBorder(geometry);
  const pad = 12;
  const inner: Box = {
    x: border.x + pad,
    y: border.y + pad,
    width: border.width - pad * 2,
    height: border.height - pad * 2,
  };
  const cx = inner.x + inner.width / 2;
  const at = (fraction: number) => inner.y + inner.height * fraction;
  const h = inner.height;
  const textWidth = inner.width * 0.84;

  const organisation = text.organisation.trim()
    ? placed(
        text.organisation.trim().toUpperCase(),
        cx,
        at(0.07),
        Math.min(5, h * 0.026)
      )
    : null;

  const titleBlock = fitTitle(
    measure,
    text.title.trim(),
    fonts.title,
    textWidth,
    h * 0.1
  );
  const titleBandTop = at(0.1);
  const titleBandHeight = h * 0.2;
  const titleLineHeight = titleBlock.size * 1.15;
  const titleTotal = titleLineHeight * titleBlock.lines.length;
  const titleStart =
    titleBandTop + (titleBandHeight - titleTotal) / 2 + titleBlock.size * 0.85;
  const title = text.title.trim()
    ? titleBlock.lines.map((line, index) =>
        placed(line, cx, titleStart + index * titleLineHeight, titleBlock.size)
      )
    : [];

  const intro = text.intro.trim()
    ? placed(text.intro.trim(), cx, at(0.4), Math.min(5.5, h * 0.026))
    : null;

  const nameBaseline = at(0.55);
  const nameRule: Rule = {
    x1: r2(cx - inner.width * 0.36),
    x2: r2(cx + inner.width * 0.36),
    y: r2(nameBaseline + h * 0.014),
  };

  const bodyBlock = fitBody(
    measure,
    text.body.trim(),
    fonts.body,
    inner.width * 0.7,
    h * 0.032,
    h * 0.15
  );
  const bodyLineHeight = bodyBlock.size * 1.45;
  const body = text.body.trim()
    ? bodyBlock.lines.map((line, index) =>
        placed(
          line,
          cx,
          at(0.64) + bodyBlock.size + index * bodyLineHeight,
          bodyBlock.size
        )
      )
    : [];

  const ruleY = at(0.88);
  const ruleWidth = inner.width * 0.24;
  const labelSize = Math.min(4.2, h * 0.02);
  const dateRule: Rule = {
    x1: r2(inner.x + inner.width * 0.04),
    x2: r2(inner.x + inner.width * 0.04 + ruleWidth),
    y: r2(ruleY),
  };
  const signatureRule: Rule = {
    x1: r2(inner.x + inner.width * 0.96 - ruleWidth),
    x2: r2(inner.x + inner.width * 0.96),
    y: r2(ruleY),
  };
  const dateLabel = text.dateLabel.trim()
    ? placed(
        text.dateLabel.trim(),
        (dateRule.x1 + dateRule.x2) / 2,
        ruleY + labelSize * 1.4,
        labelSize
      )
    : null;
  const dateText = text.dateText.trim()
    ? placed(
        text.dateText.trim(),
        (dateRule.x1 + dateRule.x2) / 2,
        ruleY - 1.8,
        labelSize * 1.15
      )
    : null;
  const signatureLabel = text.signatureLabel.trim()
    ? placed(
        text.signatureLabel.trim(),
        (signatureRule.x1 + signatureRule.x2) / 2,
        ruleY + labelSize * 1.4,
        labelSize
      )
    : null;

  return {
    border,
    inner,
    centerX: r2(cx),
    organisation,
    title,
    intro,
    nameBaseline: r2(nameBaseline),
    nameWidth: r2(inner.width * 0.7),
    nameSizeMax: r2(h * 0.095),
    body,
    nameRule,
    dateRule,
    signatureRule,
    dateLabel,
    dateText,
    signatureLabel,
    seal: showSeal
      ? { cx: r2(cx), cy: r2(at(0.85)), radius: r2(Math.min(h * 0.075, 16)) }
      : null,
  };
}
